import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { TrialStatus } from '@grove/api-client';
import { apiFetch } from '../lib/api';

const LOW_TRIAL_DAYS_THRESHOLD = 3;
const DISMISS_KEY = 'grove:trial-banner-dismissed-until';

function getDismissedUntil(): string | null {
  try {
    return sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

function setDismissedUntil(trialEndsAt: string) {
  try {
    sessionStorage.setItem(DISMISS_KEY, trialEndsAt);
  } catch {
    // Private browsing or storage disabled — the banner just won't stay dismissed.
  }
}

/**
 * Persistent trial-state banner shown at the top of every authenticated page
 * except Settings, which already has the full billing detail. Lapsed is
 * never dismissible — saving/sync are actually paused, so the reminder
 * should stay until the user resubscribes.
 */
export function TrialBanner() {
  const location = useLocation();
  // Bumped on dismiss to force a re-render — the dismissed state itself
  // lives in sessionStorage, keyed by trialEndsAt so a new trial period
  // naturally un-dismisses it.
  const [, forceUpdate] = useState(0);

  const { data: trial } = useQuery<TrialStatus>({
    queryKey: ['trial-status'],
    queryFn: () => apiFetch('/user/quota'),
    staleTime: 60_000,
  });

  if (location.pathname === '/settings') return null;
  if (!trial || trial.status === 'active') return null;

  const lapsed = trial.status === 'lapsed';
  if (!lapsed) {
    if (trial.daysRemaining === null || trial.daysRemaining > LOW_TRIAL_DAYS_THRESHOLD) return null;
    if (getDismissedUntil() === trial.trialEndsAt) return null;
  }

  return (
    <div
      role="status"
      className={`flex items-center justify-between gap-3 mx-4 mt-4 px-4 py-2.5 rounded-xl text-sm border ${
        lapsed
          ? 'bg-prism-amber/10 text-prism-amber border-prism-amber/20'
          : 'bg-surface-1 text-text-secondary border-border-subtle'
      }`}
    >
      <span>
        {lapsed
          ? 'Your trial has ended. Your library is still yours — subscribe to keep saving and syncing.'
          : `${trial.daysRemaining} day${trial.daysRemaining === 1 ? '' : 's'} left in your trial.`}
      </span>
      <div className="flex items-center gap-3 flex-shrink-0">
        <Link to="/settings" className="font-medium text-prism-violet hover:underline">
          Subscribe — $29/yr
        </Link>
        {!lapsed && (
          <button
            onClick={() => {
              if (trial.trialEndsAt) setDismissedUntil(trial.trialEndsAt);
              forceUpdate((n) => n + 1);
            }}
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}
